import React, { useState, useEffect } from 'react';
import { Metric } from 'web-vitals';
import { TIMEVALUES } from '../domain/constants';

interface VitalsState {
  CLS: number | null;
  FCP: number | null;
  INP: number | null;
  LCP: number | null;
  TTFB: number | null;
}

const initialVitals: VitalsState = {
  CLS: null,
  FCP: null,
  INP: null,
  LCP: null,
  TTFB: null,
};

export const PerformanceReport: React.FC = () => {
  const [vitals, setVitals] = useState<VitalsState>(initialVitals);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleMetric = (metric: Metric) => {
      setVitals(prev => ({
        ...prev,
        [metric.name]: metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value),
      }));
    };

    const initVitals = async () => {
      const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import('web-vitals');
      onCLS(handleMetric);
      onFCP(handleMetric);
      onINP(handleMetric);
      onLCP(handleMetric);
      onTTFB(handleMetric);
    };

    initVitals();

    const timer = setTimeout(() => setIsVisible(true), TIMEVALUES.VITALS_SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!import.meta.env.DEV || !isVisible) return null;

  const getStatus = (name: keyof VitalsState, value: number | null) => {
    if (value === null) return 'gray';
    const thresholds: Record<string, [number, number]> = {
      CLS: [100, 250],
      FCP: [1800, 3000],
      INP: [200, 500],
      LCP: [2500, 4000],
      TTFB: [800, 1800],
    };
    const [good, needsImprovement] = thresholds[name];
    if (value <= good) return 'green';
    if (value <= needsImprovement) return 'yellow';
    return 'red';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'green': return 'text-green-600';
      case 'yellow': return 'text-yellow-600';
      case 'red': return 'text-red-600';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 max-w-xs border border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Core Web Vitals</h3>
      <div className="space-y-2">
        {(Object.keys(vitals) as Array<keyof VitalsState>).map(metricName => {
          const value = vitals[metricName];
          const status = getStatus(metricName, value);
          return (
            <div key={metricName} className="flex justify-between items-center text-xs">
              <span className="text-gray-600 dark:text-gray-400">{metricName}</span>
              <span className={`font-mono font-medium ${getStatusColor(status)}`}>
                {value !== null ? `${value}ms` : '...'}
              </span>
            </div>
          );
        })}
      </div>
      <button
        onClick={() => setIsVisible(false)}
        className="mt-3 text-xs text-gray-500 hover:text-gray-700 w-full text-center"
      >
        Dismiss
      </button>
    </div>
  );
};
